import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen luxury-gradient">
      {/* Hero Section */}
      <section className="relative py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gradient mb-6">
            Privacy Policy
          </h1>
          <p className="text-xl text-secondary max-w-2xl mx-auto leading-relaxed">
            Your privacy is paramount to our commitment to extraordinary service. 
            This policy outlines how we safeguard your personal information with the same 
            precision we apply to our acoustic engineering.
          </p>
          <div className="mt-8 text-sm text-secondary/80">
            <p>Effective Date: January 1, 2026</p>
            <p>Last Updated: January 1, 2026</p>
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="glass-effect rounded-xl p-8 md:p-12 space-y-12">
            
            {/* Introduction */}
            <div>
              <h2 className="text-2xl font-semibold text-accent mb-6">Our Commitment to Your Privacy</h2>
              <div className="prose prose-invert max-w-none space-y-4 text-secondary leading-relaxed">
                <p>
                  At Parallax Cinema, we understand that exceptional service extends beyond delivering 
                  reference-quality audiovisual experiences—it encompasses safeguarding the personal 
                  information you entrust to us. This Privacy Policy explains how we collect, use, 
                  protect, and share your personal information when you interact with our services, 
                  visit our showroom, or engage with our consultation services.
                </p>
                <p>
                  By choosing Parallax Cinema, you are entering into a relationship built on trust, 
                  precision, and uncompromising standards. This extends to how we handle your privacy 
                  with the same meticulous attention to detail that defines our acoustic engineering.
                </p>
              </div>
            </div>

            {/* Information We Collect */}
            <div>
              <h2 className="text-2xl font-semibold text-accent mb-6">Information We Collect</h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-highlight mb-3">Personal Information</h3>
                  <ul className="text-secondary space-y-2 pl-4">
                    <li>• Contact details (name, email, phone number, address)</li>
                    <li>• Consultation preferences and scheduling information</li>
                    <li>• Home theater specifications and room dimensions</li>
                    <li>• Budget parameters and equipment preferences</li>
                    <li>• Installation requirements and technical specifications</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-lg font-medium text-highlight mb-3">Technical Information</h3>
                  <ul className="text-secondary space-y-2 pl-4">
                    <li>• Website usage data and analytics</li>
                    <li>• Device information and browser specifications</li>
                    <li>• IP address and geographic location (general)</li>
                    <li>• Consultation booking and appointment data</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-highlight mb-3">Professional Services Information</h3>
                  <ul className="text-secondary space-y-2 pl-4">
                    <li>• Home assessment and acoustic measurements</li>
                    <li>• Custom installation specifications</li>
                    <li>• Warranty and service history</li>
                    <li>• Payment and transaction information</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* How We Use Information */}
            <div>
              <h2 className="text-2xl font-semibold text-accent mb-6">How We Use Your Information</h2>
              <div className="prose prose-invert max-w-none space-y-4 text-secondary leading-relaxed">
                <p>
                  Your information enables us to deliver the exceptional, personalized service that defines 
                  the Parallax Cinema experience:
                </p>
                <ul className="space-y-2 pl-4">
                  <li>• **Consultation Services**: Scheduling private appointments and preparing customized presentations</li>
                  <li>• **Custom Design**: Creating bespoke home theater solutions tailored to your space and preferences</li>
                  <li>• **Professional Installation**: Coordinating white-glove installation services and technical support</li>
                  <li>• **Ongoing Support**: Providing warranty service, calibration updates, and technical assistance</li>
                  <li>• **Exclusive Communications**: Sharing product updates, technology innovations, and special events</li>
                  <li>• **Service Enhancement**: Continuously improving our offerings based on your feedback and preferences</li>
                </ul>
              </div>
            </div>

            {/* Information Sharing */}
            <div>
              <h2 className="text-2xl font-semibold text-accent mb-6">Information Sharing and Disclosure</h2>
              <div className="prose prose-invert max-w-none space-y-4 text-secondary leading-relaxed">
                <p>
                  We maintain strict confidentiality standards and share your information only in these circumstances:
                </p>
                <ul className="space-y-2 pl-4">
                  <li>• **Authorized Installation Partners**: Certified technicians requiring access for professional installation</li>
                  <li>• **Service Providers**: Trusted partners who assist with logistics, delivery, and technical support</li>
                  <li>• **Legal Requirements**: When required by law, court order, or government regulation</li>
                  <li>• **Business Continuity**: In the event of merger, acquisition, or asset transfer (with continued privacy protection)</li>
                  <li>• **Your Consent**: When you explicitly authorize sharing for specific purposes</li>
                </ul>
                <p>
                  We never sell, rent, or trade your personal information to third parties for marketing purposes.
                </p>
              </div>
            </div>

            {/* Data Security */}
            <div>
              <h2 className="text-2xl font-semibold text-accent mb-6">Data Security and Protection</h2>
              <div className="prose prose-invert max-w-none space-y-4 text-secondary leading-relaxed">
                <p>
                  We employ enterprise-grade security measures to protect your personal information:
                </p>
                <ul className="space-y-2 pl-4">
                  <li>• **Encryption**: Advanced encryption for data transmission and storage</li>
                  <li>• **Access Controls**: Strict employee access protocols and authentication requirements</li>
                  <li>• **Secure Facilities**: Physical security measures for our showroom and data storage</li>
                  <li>• **Regular Monitoring**: Continuous security assessments and system monitoring</li>
                  <li>• **Incident Response**: Comprehensive protocols for addressing any security concerns</li>
                </ul>
              </div>
            </div>

            {/* Your Rights */}
            <div>
              <h2 className="text-2xl font-semibold text-accent mb-6">Your Privacy Rights</h2>
              <div className="prose prose-invert max-w-none space-y-4 text-secondary leading-relaxed">
                <p>You have comprehensive control over your personal information:</p>
                <ul className="space-y-2 pl-4">
                  <li>• **Access**: Request copies of your personal information in our possession</li>
                  <li>• **Correction**: Update or correct any inaccurate information</li>
                  <li>• **Deletion**: Request removal of your personal information (subject to legal requirements)</li>
                  <li>• **Portability**: Receive your information in a machine-readable format</li>
                  <li>• **Opt-out**: Unsubscribe from marketing communications at any time</li>
                  <li>• **Restriction**: Limit how we process your information</li>
                </ul>
                <p>
                  To exercise these rights, contact us at <Link to="/contact" className="text-accent hover:text-highlight">privacy@parallaxcinema.com</Link> or 
                  call our consultation line at +1 (555) 123-4567.
                </p>
              </div>
            </div>

            {/* Cookies */}
            <div>
              <h2 className="text-2xl font-semibold text-accent mb-6">Cookies and Website Technologies</h2>
              <div className="prose prose-invert max-w-none space-y-4 text-secondary leading-relaxed">
                <p>
                  Our website uses cookies and similar technologies to enhance your experience:
                </p>
                <ul className="space-y-2 pl-4">
                  <li>• **Essential Cookies**: Required for website functionality and security</li>
                  <li>• **Analytics Cookies**: Help us understand website usage and improve performance</li>
                  <li>• **Preference Cookies**: Remember your settings and customization choices</li>
                </ul>
                <p>
                  You can manage cookie preferences through your browser settings. Note that disabling certain 
                  cookies may affect website functionality.
                </p>
              </div>
            </div>

            {/* Updates */}
            <div>
              <h2 className="text-2xl font-semibold text-accent mb-6">Policy Updates</h2>
              <div className="prose prose-invert max-w-none space-y-4 text-secondary leading-relaxed">
                <p>
                  We may update this Privacy Policy to reflect changes in our services, legal requirements, 
                  or industry best practices. Material changes will be communicated through:
                </p>
                <ul className="space-y-2 pl-4">
                  <li>• **Website Notice**: Prominent notification on our homepage</li>
                  <li>• **Direct Communication**: Email notification to existing clients</li>
                  <li>• **Consultation Updates**: Discussion during scheduled appointments</li>
                </ul>
                <p>
                  Continued use of our services after any updates constitutes acceptance of the revised policy.
                </p>
              </div>
            </div>

            {/* Contact */}
            <div className="border-t border-secondary/20 pt-8">
              <h2 className="text-2xl font-semibold text-accent mb-6">Contact Information</h2>
              <div className="prose prose-invert max-w-none space-y-4 text-secondary leading-relaxed">
                <p>
                  For questions about this Privacy Policy or your personal information, please contact us:
                </p>
                <div className="grid md:grid-cols-2 gap-6 mt-6">
                  <div>
                    <h4 className="text-accent font-medium mb-2">Privacy Officer</h4>
                    <p>Email: privacy@parallaxcinema.com</p>
                    <p>Phone: +1 (555) 123-4567</p>
                  </div>
                  <div>
                    <h4 className="text-accent font-medium mb-2">Showroom</h4>
                    <p>205 Diamond Boulevard, Unit 339</p>
                    <p>Anchorage, AK 99575</p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy; 